import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import FeeTierModalActiveIndicator from 'src/components/Modals/FeeTierModal/FeeTierModalActiveIndicator';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { FeeTierTableRow } from 'src/components/Modals/FeeTierModal/types';
import { cj } from 'src/utils/classNames';

interface Props {
	tableRows: FeeTierTableRow[];
	userVolumeTierIndex: number;
	isUserOnHighestVolumeTier: boolean;
}

interface FeeTierDataRow extends FeeTierTableRow {
	isActive: boolean;
	showActiveIndicator: boolean;
}

const BODY_CELL_CLASSNAME = 'relative h-auto';

const columnHelper = createColumnHelper<FeeTierDataRow>();

const columns = [
	columnHelper.accessor('tier', {
		header: 'Tier',
		meta: {
			width: { fixedPx: 40 },
			bodyCellClassName: cj('text-left whitespace-nowrap', BODY_CELL_CLASSNAME),
		},
		cell: (info) => {
			const row = info.row.original;
			return (
				<>
					{row.showActiveIndicator && <FeeTierModalActiveIndicator />}
					<span className={cj('ml-2', row.isActive && 'text-purple-link')}>
						{info.getValue()}
					</span>
				</>
			);
		},
	}),
	columnHelper.accessor('volume', {
		header: '30D Volume',
		meta: {
			headerCellClassName: 'text-right',
			bodyCellClassName: cj('text-right', BODY_CELL_CLASSNAME),
		},
		cell: (info) => info.getValue(),
	}),
	columnHelper.accessor('takerFee', {
		header: 'Taker',
		meta: {
			headerCellClassName: 'text-right',
			bodyCellClassName: cj('text-right', BODY_CELL_CLASSNAME),
		},
		cell: (info) => info.getValue(),
	}),
	columnHelper.accessor('makerFee', {
		header: () => (
			<DefinitionTooltip definitionId="feeTierMakerRebate">
				Maker
			</DefinitionTooltip>
		),
		meta: {
			headerCellClassName: 'text-right',
			bodyCellClassName: cj('text-right', BODY_CELL_CLASSNAME),
		},
		cell: (info) => info.getValue(),
	}),
];

export default function FeeTierModalTable({
	tableRows,
	userVolumeTierIndex,
	isUserOnHighestVolumeTier,
}: Props) {
	const data = useMemo<FeeTierDataRow[]>(
		() =>
			tableRows.map((row, index) => ({
				...row,
				isActive: index === userVolumeTierIndex,
				showActiveIndicator:
					index === userVolumeTierIndex && !isUserOnHighestVolumeTier,
			})),
		[tableRows, userVolumeTierIndex, isUserOnHighestVolumeTier]
	);

	return (
		<div className="overflow-hidden rounded-sm border border-stroke-divider [&_[data-slot=table-container]]:overflow-hidden">
			<DataTable columns={columns} data={data} className="w-full table-fixed" />
		</div>
	);
}
